/* Pamo Home Discovery v1.0.0. Isolated UI; same-origin GET only; no cart/payment overrides. */
(() => {
  'use strict';
  const TAG = 'pamo-home-discovery';
  if (!window.customElements || customElements.get(TAG)) return;
  const scripts = new Map();
  let privacyReady;
  const allowed = () => {
    try {
      const p = window.Shopify?.customerPrivacy;
      return p?.preferencesProcessingAllowed?.() === true && p?.analyticsProcessingAllowed?.() === true;
    } catch (_) { return false; }
  };
  const loadPrivacy = () => {
    if (window.Shopify?.customerPrivacy) return Promise.resolve();
    if (!privacyReady) privacyReady = new Promise(resolve => {
      const timer = setTimeout(resolve, 5000);
      try {
        if (!window.Shopify?.loadFeatures) { clearTimeout(timer); resolve(); return; }
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => { clearTimeout(timer); resolve(); });
      } catch (_) { clearTimeout(timer); resolve(); }
    });
    return privacyReady;
  };
  const readViewed = () => {
    if (!allowed()) return [];
    try {
      const raw = localStorage.getItem('cc-recently-viewed') || '[]';
      if (raw.length > 4096) return [];
      const value = JSON.parse(raw);
      if (!Array.isArray(value)) return [];
      return [...new Set(value.slice(0, 12).filter(v => typeof v === 'number' || typeof v === 'string').map(String).filter(v => /^\d{6,16}$/.test(v) && Number.isSafeInteger(Number(v)) && Number(v) > 0))];
    } catch (_) { return []; }
  };
  const ensureScript = src => {
    if (!src) return Promise.resolve();
    const url = new URL(src, location.origin);
    if ([...document.scripts].some(s => s.src && new URL(s.src, location.origin).pathname === url.pathname)) return Promise.resolve();
    if (scripts.has(url.href)) return scripts.get(url.href);
    const promise = new Promise(resolve => {
      const script = document.createElement('script');
      script.src = url.href; script.async = false;
      script.onload = script.onerror = resolve;
      document.head.append(script);
    });
    scripts.set(url.href, promise);
    return promise;
  };
  const rekey = (cards, prefix) => {
    const mapping = new Map();
    cards.forEach(card => card.querySelectorAll('[id]').forEach(el => {
      const old = el.id; const next = `${prefix}-${old}`;
      mapping.set(old, next); el.id = next;
    }));
    cards.forEach(card => {
      card.querySelectorAll('script').forEach(el => { if (!['application/json', 'application/ld+json'].includes(el.type)) el.remove(); });
      card.querySelectorAll('*').forEach(el => {
        ['for', 'form', 'list', 'aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(attr => {
          const value = el.getAttribute(attr);
          if (value) el.setAttribute(attr, value.split(/\s+/).map(id => mapping.get(id) || id).join(' '));
        });
        const href = el.getAttribute('href');
        if (href?.startsWith('#') && mapping.has(href.slice(1))) el.setAttribute('href', `#${mapping.get(href.slice(1))}`);
        if (el.matches('input[type="radio"][name]') && !el.name.startsWith('options[')) el.name = `${prefix}-${el.name}`;
      });
    });
    return cards;
  };
  class PamoDiscovery extends HTMLElement {
    connectedCallback() {
      if (this._running) return;
      this._running = true; this._signature = null; this._epoch = (this._epoch || 0) + 1; this._history = []; this._requests = new Set();
      this._events = new AbortController();
      this._rows = [...this.querySelectorAll('.pd-shelf')].map(el => ({
        el, mode: el.dataset.pdMode, items: el.querySelector('[data-pd-items]'),
        base: el.querySelector('[data-pd-items]').innerHTML,
        title: el.querySelector('[data-pd-heading]').textContent,
        link: el.querySelector('[data-pd-view-all]').getAttribute('href'), linkText: el.querySelector('[data-pd-view-all]').textContent,
        limit: Math.min(12, Math.max(4, Number(el.dataset.pdLimit) || 8)),
        loaded: false, busy: false, personalBusy: false, personalized: false, pages: 0, next: null, near: false
      }));
      const signal = this._events.signal;
      this.addEventListener('click', event => {
        const arrow = event.target.closest('[data-pd-scroll]');
        if (arrow && this.contains(arrow)) {
          const row = arrow.closest('.pd-shelf').querySelector('[data-pd-items]');
          row.scrollBy({ left: Number(arrow.dataset.pdScroll) * row.clientWidth * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
        const more = event.target.closest('[data-pd-more]');
        if (more && this.contains(more)) { const state = this._rows.find(s => s.el.contains(more)); if (state) this._loadCollection(state, true); }
      }, { signal });
      window.addEventListener('storage', event => { if (event.key === 'cc-recently-viewed') this._refreshPrivacy(); }, { signal });
      document.addEventListener('visitorConsentCollected', () => this._refreshPrivacy(), { signal });
      window.addEventListener('pageshow', () => this._refreshPrivacy(), { signal });
      try {
        const assets = JSON.parse(this.querySelector('[data-pd-assets]')?.textContent || '{}');
        (async () => { for (const src of assets.scripts || []) await ensureScript(src); })().catch(() => {});
      } catch (_) { /* Existing theme assets remain the fallback. */ }
      this._rows.forEach(state => {
        const sort = state.el.dataset.pdSort;
        if (sort && sort !== 'manual' && state.mode !== 'recent' && state.mode !== 'related') {
          const href = new URL(state.link, location.origin); href.searchParams.set('sort_by', sort);
          state.el.querySelector('[data-pd-view-all]').href = href.href; state.link = href.href;
        }
      });
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const state = this._rows.find(s => s.el === entry.target); if (!state) return;
          state.near = true;
          if (state.mode === 'related') this._loadPersonal(state);
          else if (state.mode !== 'recent' && (state.mode === 'explore' || state.el.dataset.pdSort !== 'manual')) this._loadCollection(state);
          this._observer.unobserve(state.el);
        }), { rootMargin: '350px 0px' });
        this._rows.filter(s => s.mode !== 'recent').forEach(s => this._observer.observe(s.el));
      } else {
        this._rows.forEach(s => { s.near = true; if (s.mode !== 'related' && s.mode !== 'recent' && (s.mode === 'explore' || s.el.dataset.pdSort !== 'manual')) this._loadCollection(s); });
      }
      if (this.dataset.personalize === 'true') loadPrivacy().then(() => this.isConnected && this._refreshPrivacy());
    }
    disconnectedCallback() {
      this._events?.abort(); this._observer?.disconnect(); this._requests?.forEach(r => r.controller.abort());
      this._epoch++; this._rows?.filter(s => s.mode === 'recent' || s.mode === 'related').forEach(s => this._restore(s)); this._signature = null; this._running = false;
    }
    _url(path, section, params = {}) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw new Error('Foreign origin');
      url.searchParams.set('section_id', section);
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      return url;
    }
    async _fetch(url, personal = false) {
      const controller = new AbortController(); const request = { controller, personal };
      this._requests.add(request); const timer = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', mode: 'same-origin', signal: controller.signal });
        if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Feed unavailable');
        const text = await response.text(); if (text.length > 2500000 || text.includes('Liquid error')) throw new Error('Feed render error');
        const doc = new DOMParser().parseFromString(text, 'text/html'); const feed = doc.querySelector('[data-pd-feed]');
        if (!feed || !feed.querySelector('ul')) throw new Error('Missing feed');
        return { cards: [...feed.querySelector('ul').children].filter(el => el.matches('li[data-pd-product-id]') && /^\d+$/.test(el.dataset.pdProductId)), next: feed.dataset.pdNext || null };
      } finally { clearTimeout(timer); this._requests.delete(request); }
    }
    _replace(state, cards, append = false) {
      const ids = new Set(append ? [...state.items.children].map(el => el.dataset.pdProductId) : []);
      const clean = cards.filter(card => { const id = card.dataset.pdProductId; if (ids.has(id)) return false; ids.add(id); return true; });
      rekey(clean, `${state.el.id}-page${state.pages}-${state.personalized ? 'personal' : 'general'}`);
      const fragment = document.createDocumentFragment(); clean.forEach(card => fragment.append(document.importNode(card, true)));
      if (append) state.items.append(fragment); else state.items.replaceChildren(fragment);
      state.el.hidden = false;
      return clean.length;
    }
    async _loadCollection(state, append = false) {
      if (!this.isConnected || state.busy || (!append && state.loaded) || (append && (!state.next || state.pages >= 4))) return;
      const source = state.el.dataset.pdCollection; if (!source) return;
            state.busy = true; const button = state.el.querySelector('[data-pd-more]');
      const status = state.el.querySelector('[data-pd-status]');
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      try {
        const params = {}; const sort = state.el.dataset.pdSort;
        if (['best-selling', 'created-descending'].includes(sort)) params.sort_by = sort;
        const url = this._url(append ? state.next : source, state.mode === 'offers' ? 'pamo-discovery-offers' : 'pamo-discovery-feed', params);
        const expectedPath = new URL(source, location.origin).pathname;
        if (url.pathname !== expectedPath) throw new Error('Unexpected collection path');
        const result = await this._fetch(url);
        if (!this.isConnected || !this._rows.includes(state)) return;
        state.pages = append ? state.pages + 1 : 1;
        const selected = state.mode === 'explore' ? result.cards : result.cards.slice(0, state.limit);
        let added = 0;
        if (selected.length) {
          added = this._replace(state, selected, append);
          if (state.el.dataset.pdHeadingTarget) state.el.querySelector('[data-pd-heading]').textContent = state.el.dataset.pdHeadingTarget;
        }
        state.loaded = true; state.next = result.next ? this._url(result.next, 'pamo-discovery-feed', params).href : null;
        if (button) button.hidden = !state.next || state.pages >= 4;
        if (status) status.textContent = append ? (added ? `Se agregaron ${added} productos.` : 'No hay productos disponibles en este grupo. Puedes continuar explorando.') : '';
      } catch (_) {
        state.el.dataset.pdLoad = 'fallback';
        if (status && append) status.textContent = 'No fue posible cargar más. Inténtalo de nuevo o entra a la colección.';
      } finally { state.busy = false; if (button) { button.disabled = false; button.removeAttribute('aria-busy'); } }
    }
    _restore(state) {
      if (state.personalized) { state.items.innerHTML = state.base; state.el.querySelector('[data-pd-heading]').textContent = state.title; state.el.querySelector('[data-pd-view-all]').setAttribute('href', state.link); state.el.querySelector('[data-pd-view-all]').textContent = state.linkText; }
      state.personalized = false; state.personalBusy = false;
      if (state.mode === 'recent') { state.items.replaceChildren(); state.el.hidden = true; }
    }
    _refreshPrivacy() {
      if (!this.isConnected) return;
      const history = this.dataset.personalize === 'true' ? readViewed() : [];
      const signature = `${allowed() ? 'allowed' : 'denied'}:${history.join(',')}`;
      if (signature === this._signature) return;
      this._signature = signature;
      this._epoch++; this._requests.forEach(r => { if (r.personal) r.controller.abort(); });
      this._rows.filter(s => s.mode === 'recent' || s.mode === 'related').forEach(s => this._restore(s));
      this._history = history;
      if (!this._history.length) return;
      this._rows.filter(s => s.mode === 'recent' || (s.mode === 'related' && s.near)).forEach(s => this._loadPersonal(s));
    }
    async _loadPersonal(state) {
      if (!this.isConnected || state.personalBusy || state.personalized || !this._history.length || !allowed() || this.dataset.personalize !== 'true') return;
      state.personalBusy = true; const epoch = this._epoch; const history = this._history.slice();
      try {
        const root = this.dataset.root.endsWith('/') ? this.dataset.root : `${this.dataset.root}/`;
        const url = state.mode === 'recent'
          ? this._url(`${root}search`, 'pamo-discovery-feed', { type: 'product', q: history.map(id => `id:${id}`).join(' OR '), 'options[unavailable_products]': 'hide' })
          : this._url(`${root}recommendations/products`, 'pamo-discovery-feed', { product_id: history[0], intent: 'related', limit: '10' });
        const result = await this._fetch(url, true);
        if (!this.isConnected || epoch !== this._epoch || !allowed()) return;
        let cards = result.cards;
        if (state.mode === 'recent') cards = cards.filter(c => history.includes(c.dataset.pdProductId)).sort((a, b) => history.indexOf(a.dataset.pdProductId) - history.indexOf(b.dataset.pdProductId));
        else cards = cards.filter(c => c.dataset.pdProductId !== history[0]);
        cards = cards.slice(0, state.limit); if (!cards.length) return;
        state.personalized = true; this._replace(state, cards);
        if (state.mode === 'related') state.el.querySelector('[data-pd-heading]').textContent = state.el.dataset.pdPersonalTitle || 'Relacionado con lo que viste';
        const link = state.el.querySelector('[data-pd-view-all]'); link.setAttribute('href', this.dataset.catalog); link.textContent = 'Ver catálogo →';
        state.el.dataset.pdLoad = 'personalized';
      } catch (_) { state.el.dataset.pdLoad = 'fallback'; }
      finally { if (epoch === this._epoch) state.personalBusy = false; }
    }
  }
  customElements.define(TAG, PamoDiscovery);
})();
